import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

// Mecânica pura de widget flutuante, emprestada do Lucas SEM alterar nada lá:
// arrastar sem re-renderizar (move por `transform`, senão o canvas do flow
// trava durante o arrasto) e prender a bolinha dentro da tela. A regra da Vera
// é importar do Lucas e nunca mexer nele.
import useDraggable from "@/components/support/useDraggable";
import { clampPosition } from "@/components/support/useLucasWidgetPrefs";
// A superfície é compartilhada de propósito: ver o comentário no atalho.
import { LUCAS_SURFACE as SUPERFICIE } from "@/components/support/lucasTokens";

import { VERA_ACCENT_TEXT } from "./veraTokens";

import useVeraWidgetPrefs, {
  tamanhoFoiAlterado,
  TAMANHO_PADRAO,
} from "./useVeraWidgetPrefs";

/**
 * O atalho da Vera, para o suporte investigar sem sair da tela onde está.
 *
 * ## O que fica montado
 *
 * Só este arquivo: um botão. A conversa inteira (markdown, balões, histórico)
 * está atrás de um `lazy()` e só desce da rede quando alguém abre.
 *
 * ## Por que não é uma aba dentro do Lucas
 *
 * A aba moraria no painel que TODO cliente abre: um erro ali quebraria o
 * atendimento de todo mundo, por causa de uma tela que oito pessoas usam. E o
 * painel dele tem 380 por 560, apertado de propósito.
 *
 * ## O que foi deixado de fora de propósito
 *
 * A pulsada de descoberta. Aquele anel existe para o cliente descobrir que o
 * Lucas existe; quem abre a Vera já sabe que ela existe.
 *
 * ## Por que some na página da Vera
 *
 * Lá a conversa já é a tela inteira, e a bolinha mostraria o mesmo diálogo duas
 * vezes, uma por cima da outra. Mesma razão pela qual a do Lucas some na home.
 */

const VeraWidgetPanel = lazy(() => import("./VeraWidgetPanel"));

const TAMANHO_DA_BOLINHA = 56;
const VAO = 12;
const MARGEM = 8;

/**
 * Onde o painel cabe, dado onde a bolinha está.
 *
 * Abre ACIMA da bolinha, que é o natural para um atalho no rodapé. Mas ela é
 * arrastável: encostada no topo, não sobra altura em cima, e aí o painel abre
 * para baixo em vez de passar por cima dela.
 */
export function posicaoDoPainel(position, viewport, size) {
  // A largura é cortada pela tela antes de tudo: sem isto, um painel
  // redimensionado num monitor grande e reaberto num notebook sairia pela
  // esquerda, e a alça de redimensionar, que mora no canto superior esquerdo,
  // sairia junto. Ficaria grande demais e sem como encolher.
  const width = Math.max(
    TAMANHO_DA_BOLINHA,
    Math.min(size.width, viewport.width - 2 * MARGEM),
  );

  const right = Math.min(
    Math.max(position.right, MARGEM),
    Math.max(MARGEM, viewport.width - width - MARGEM),
  );

  const acima = viewport.height - position.bottom - TAMANHO_DA_BOLINHA - VAO - MARGEM;
  const abaixo = position.bottom - VAO - MARGEM;

  if (acima >= size.height || acima >= abaixo) {
    return {
      right,
      width,
      bottom: position.bottom + TAMANHO_DA_BOLINHA + VAO,
      height: Math.max(TAMANHO_DA_BOLINHA, Math.min(size.height, acima)),
    };
  }

  const height = Math.max(TAMANHO_DA_BOLINHA, Math.min(size.height, abaixo));
  return { right, width, bottom: position.bottom - VAO - height, height };
}

export default function VeraBubble({ userId }) {
  const location = useLocation();
  const { prefs, moverPara, redimensionarPara, esconder } = useVeraWidgetPrefs(userId);
  const [aberto, setAberto] = useState(false);
  const [viewport, setViewport] = useState(() => ({
    width: typeof window === "undefined" ? 1280 : window.innerWidth,
    height: typeof window === "undefined" ? 800 : window.innerHeight,
  }));

  useEffect(() => {
    const aoRedimensionar = () =>
      setViewport({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", aoRedimensionar);
    return () => window.removeEventListener("resize", aoRedimensionar);
  }, []);

  const position = clampPosition(prefs.position, viewport);

  const { nodeRef, consumeDrag, dragProps } = useDraggable({
    position,
    onCommit: (nova) => moverPara(clampPosition(nova, viewport)),
  });

  const aoClicar = useCallback(() => {
    // Soltar a bolinha num lugar novo não pode abrir o painel junto.
    if (consumeDrag()) return;
    setAberto((atual) => !atual);
  }, [consumeDrag]);

  // Na página da Vera o atalho não faz sentido: a conversa já é a tela.
  if (location.pathname.startsWith("/internal/platform/vera")) return null;
  if (prefs.hidden) return null;

  const painel = posicaoDoPainel(position, viewport, prefs.size);

  return (
    <>
      {aberto && (
        <div
          style={{ position: "fixed", right: painel.right, bottom: painel.bottom, zIndex: 50 }}
        >
          <Suspense fallback={null}>
            <VeraWidgetPanel
              size={{ width: painel.width, height: painel.height }}
              redimensionado={tamanhoFoiAlterado(prefs.size)}
              onTamanhoPadrao={() => redimensionarPara(TAMANHO_PADRAO)}
              onRedimensionar={redimensionarPara}
              onFechar={() => setAberto(false)}
              onEsconder={() => {
                setAberto(false);
                esconder();
              }}
            />
          </Suspense>
        </div>
      )}

      <button
        ref={nodeRef}
        type="button"
        onClick={aoClicar}
        {...dragProps}
        title="Vera: diagnóstico de chamado"
        style={{
          position: "fixed",
          right: position.right,
          bottom: position.bottom,
          width: TAMANHO_DA_BOLINHA,
          height: TAMANHO_DA_BOLINHA,
          zIndex: 50,
          touchAction: "none",
        }}
        /*
         * Mesma forma e mesma superfície do atalho do Lucas: os dois moram no
         * mesmo canto, um em cima do outro, e chips de famílias diferentes
         * fariam parecer dois produtos. Quem distingue é a COR do ícone, que
         * se lê de longe sem precisar ler nada.
         */
        className={`flex items-center justify-center rounded-[18px] shadow-[0_8px_26px_rgba(0,0,0,.18)] transition-opacity hover:opacity-90 ${SUPERFICIE} ${VERA_ACCENT_TEXT}`}
      >
        {/* A letra, e não um ícone: o atalho do Lucas já é um ícone, e dois
            desenhos pequenos lado a lado se confundem mais que uma letra ao
            lado de um desenho. O `aria-hidden` evita o leitor de tela anunciar
            "V" solto, já que o nome inteiro está no title do botão. */}
        <span aria-hidden="true" className="text-xl font-bold leading-none">
          V
        </span>
      </button>
    </>
  );
}
